import { useEffect, useRef, useState } from 'preact/hooks';
import { put } from '../api';
import { Button, Card, Pill, toast, useAction } from '../ui';

/** Mirrors StoryData in @saga/spec. */
export interface StoryData {
	title: string;
	characters: unknown[];
	locations: unknown[];
	characterLocations: unknown[];
}

const pretty = (v: unknown) => JSON.stringify(v, null, 2);

/**
 * The compiled story as one JSON document. During a compile it is read-only and fills in as each
 * pass saves its results; otherwise it can be edited, pasted or loaded from a file and saved back.
 */
export function StoryJson(p: { storyId: string; title: string; data: StoryData; compiling: boolean; reload: () => void }) {
	const [text, setText] = useState(pretty(p.data));
	const [dirty, setDirty] = useState(false);
	const [errors, setErrors] = useState<Array<{ path: string; message: string }>>([]);
	const [issues, setIssues] = useState<Array<{ part: string; severity: string; message: string }>>([]);
	const file = useRef<HTMLInputElement>(null);
	// Follow the server (a running compile, or a save) unless the GM has unsaved edits.
	useEffect(() => {
		if (!dirty || p.compiling) {
			setText(pretty(p.data));
			setDirty(false);
		}
	}, [JSON.stringify(p.data), p.compiling]);
	let parseError = '';
	try {
		JSON.parse(text);
	} catch (e: any) {
		parseError = e.message;
	}
	const edit = (t: string) => {
		setText(t);
		setDirty(true);
		setErrors([]);
	};
	const [save, saving] = useAction(async () => {
		try {
			const r = await put(`/api/stories/${p.storyId}/data`, JSON.parse(text));
			setErrors([]);
			setIssues(Object.entries(r.issues as Record<string, Array<{ severity: string; message: string }>>).flatMap(([part, list]) => list.map((i) => ({ part, ...i }))));
			setDirty(false);
			toast('Saved.', 'ok');
			p.reload();
		} catch (e: any) {
			if (e.data?.errors) setErrors(e.data.errors);
			else throw e;
		}
	});
	const download = () => {
		const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
		const a = document.createElement('a');
		a.href = url;
		a.download = `${(p.title || 'story').replace(/[^\w-]+/g, '_').toLowerCase()}.json`;
		a.click();
		URL.revokeObjectURL(url);
	};
	const load = async (f: File | undefined) => {
		if (!f) return;
		edit(await f.text());
		toast(`Loaded ${f.name}. Review it, then save.`, 'info');
	};
	const counts: Array<[string, unknown[]]> = [
		['characters', p.data.characters],
		['locations', p.data.locations],
		['character locations', p.data.characterLocations],
	];
	return (
		<div class="two-col wide-left">
			<Card
				title="Story JSON"
				actions={
					<div class="row">
						{counts.map(([label, list]) => (
							<Pill key={label} tone={list.length ? 'ok' : 'muted'}>
								{list.length} {label}
							</Pill>
						))}
					</div>
				}
			>
				{p.compiling ? (
					<p class="callout small">Compiling: each part appears here as it is found, after every section of the story. Editing opens when the compile finishes.</p>
				) : null}
				<textarea class="mono story-json" rows={28} value={text} readOnly={p.compiling} spellcheck={false} aria-label="Story JSON" onInput={(e) => edit(e.currentTarget.value)} />
				{parseError && dirty ? <p class="bad small">Not valid JSON: {parseError}</p> : null}
				{errors.length ? (
					<ul class="issues">
						{errors.map((e, i) => (
							<li key={i} class="bad">
								<code>{e.path || '(document)'}</code> {e.message}
							</li>
						))}
					</ul>
				) : null}
				<div class="row">
					<Button onClick={() => save()} busy={saving} disabled={p.compiling || !dirty || !!parseError}>
						Save JSON
					</Button>
					<Button variant="ghost" onClick={() => edit(pretty(JSON.parse(text)))} disabled={p.compiling || !!parseError}>
						Format
					</Button>
					<Button variant="ghost" onClick={() => (setText(pretty(p.data)), setDirty(false), setErrors([]))} disabled={!dirty}>
						Revert
					</Button>
					<Button variant="ghost" onClick={download}>
						Download
					</Button>
					<Button variant="ghost" onClick={() => file.current?.click()} disabled={p.compiling}>
						Load file…
					</Button>
					<input ref={file} type="file" accept="application/json,.json" hidden onChange={(e) => load(e.currentTarget.files?.[0])} />
				</div>
			</Card>
			<div>
				<Card title="Format">
					<p class="small">One document with four keys. Ids are lowercase snake_case and must match across parts.</p>
					<ul class="list small">
						<li>
							<code>title</code>: the story's title.
						</li>
						<li>
							<code>characters</code>: <code>{'{id, name, aliases, tier, role, description, inventory}'}</code>, with <code>tier</code> being main, secondary or peripheral and <code>inventory</code> a list of <code>{'{id, name, description, quantity}'}</code>. <code>appearsIn</code> is worked out from the character locations when you save.
						</li>
						<li>
							<code>locations</code>: <code>{'{id, name, description, parent}'}</code>, where <code>parent</code> is the id of the larger location it is inside, or null.
						</li>
						<li>
							<code>characterLocations</code>: moments in story order, <code>{'{id, label, placements: [{character, location, inferred, conditions}]}'}</code>. A placement that moves a character from somewhere else can have <code>conditions</code>, <code>{'{id, kind, description, item, with}'}</code>: what must be true before they can make that move (<code>kind</code> item: they carry <code>item</code>; with: <code>with</code> is with them; event: the description has happened).
						</li>
					</ul>
				</Card>
				{issues.length ? (
					<Card title="To check after saving">
						<ul class="issues">
							{issues.map((i, n) => (
								<li key={n} class={i.severity === 'error' ? 'bad' : 'warn'}>
									<span class="muted">{i.part}:</span> {i.message}
								</li>
							))}
						</ul>
					</Card>
				) : null}
			</div>
		</div>
	);
}
